import React, {memo, useEffect, useMemo, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Hub} from 'yougile-model'
import {Backend} from '@common/backend'
import {FilePreview} from '@common/file-preview'
import {Task} from '@extensions/task'
import {Delete, Upload} from './actions'

const useAutoImage = (hub: Hub): string | undefined => {
  const timestampRef = useRef<number>(0)
  const message = hub.chat?.messages.get(timestampRef.current)
  const messageCount = hub.chat?.messages.size

  // Auto url should be updated:
  // - If a message it is acquired from has been changed or deleted.
  // - If there are more (new) messages in chat that may contain images.
  return useMemo(() => {
    const autoRes = Task.findLastFirstMessageWithImage(hub)
    if (autoRes) {
      timestampRef.current = autoRes[1].timestamp
      return autoRes[0]
    } else {
      return undefined
    }
  }, [
    message?.timestamp,
    message?.edited?.timestamp,
    message?.deleted,
    messageCount
  ])
}

const useLoadedImgSrc = (url: string | undefined): string | undefined => {
  const [img] = useState(() => new Image())
  const [src, setSrc] = useState<string | undefined>(url)

  useEffect(() => {
    if (url) {
      img.src = url
      img.onload = () => setSrc(url)
      return () => {
        img.src = ''
        img.onload = null
      }
    } else {
      setSrc(url)
      return undefined
    }
  }, [img, url])

  return src
}

const TwTaskImageWrapper = tw.div`
  pt-8
  px-8
`

const TwTaskImage = tw.div`
  relative
  flex
  w-full
  min-h-64
  text-center
  overflow-hidden
  rounded-4
`

const TwBg = tw.div`
  absolute
  w-full
  h-full
  bg-no-repeat
  bg-cover
  bg-center
  scale-110
  [filter:blur(5px)]
`

const TwImg = tw.img`
  relative
  m-auto
  pointer-events-none
`

interface TaskImageProps {
  hub: Hub
  className?: string
  showActions?: boolean
  maxHeight?: number
  isAutoTaskCover?: boolean
}

interface TaskImageBaseProps extends TaskImageProps {
  src: string | undefined
}

const TaskImageBase = memo(
  ({hub, src, className, showActions, maxHeight = 160}: TaskImageBaseProps) => (
    <TwTaskImageWrapper>
      <TwTaskImage className={className} style={{maxHeight}}>
        <TwBg style={{backgroundImage: `url('${src}')`}} />
        <TwImg src={src} style={{maxHeight}} alt="" />
        {showActions && (
          <>
            <Delete hub={hub} className="absolute right-8 bottom-8" />
            <Upload hub={hub} className="absolute right-40 bottom-8" />
          </>
        )}
      </TwTaskImage>
    </TwTaskImageWrapper>
  )
)

const ManualTaskImage = (props: TaskImageProps) => {
  const url = FilePreview.previewFromUrl(
    Backend.toDataUrl(props.hub.data.image)
  )
  const src = useLoadedImgSrc(url)
  return src ? <TaskImageBase {...props} src={src} /> : null
}

const AutoTaskImage = (props: TaskImageProps) => {
  const url = FilePreview.previewFromUrl(
    Backend.toDataUrl(useAutoImage(props.hub))
  )
  const src = useLoadedImgSrc(url)
  return src ? <TaskImageBase {...props} src={src} /> : null
}

export const TaskImage = (props: TaskImageProps) => {
  if (
    !props.hub.data.image &&
    props.hub.data.image !== '' &&
    props.isAutoTaskCover
  ) {
    return <AutoTaskImage {...props} />
  } else {
    return <ManualTaskImage {...props} />
  }
}
